import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Minimize2,
  Maximize2,
  RefreshCw,
  FileAudio,
  Timer,
  SkipForward,
  Play,
  Pause
} from 'lucide-react';
import { getProcessingQueueDetails } from '@/lib/api';

/**
 * Processing Status Viewer Component
 * 
 * Real-time monitoring of call processing with:
 * - Table view with File No, Filename, Size, Status, Remarks, Time Taken
 * - Color-coded status: Processing (Orange), Skipped (Red), Processed (Green)
 * - Pagination (500 per page)
 * - Auto-scroll to current processing call
 * - Minimizable for background monitoring
 */
export function ProcessingStatusViewer({ 
  open, 
  onClose,
  isMinimized = false,
  onMinimize,
  autoRefresh = true,
  refreshInterval = 2000
}) {
  const [queueData, setQueueData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const scrollRef = useRef(null);
  const processingRowRef = useRef(null);

  const fetchQueueDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await getProcessingQueueDetails(currentPage, 500, statusFilter === 'all' ? null : statusFilter);
      setQueueData(data);
      
      // Auto-scroll to processing item
      if (autoScrollEnabled && data.current_processing && processingRowRef.current) {
        processingRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } catch (error) {
      console.error('Failed to fetch queue details:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, statusFilter, autoScrollEnabled]);

  // Initial fetch and auto-refresh
  useEffect(() => {
    if (open) {
      fetchQueueDetails();
      
      if (autoRefresh) {
        const interval = setInterval(fetchQueueDetails, refreshInterval);
        return () => clearInterval(interval);
      }
    }
  }, [open, fetchQueueDetails, autoRefresh, refreshInterval]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      processing: { color: 'bg-orange-500 text-white', icon: <Loader2 className="h-3 w-3 animate-spin" /> },
      completed: { color: 'bg-green-500 text-white', icon: <CheckCircle2 className="h-3 w-3" /> },
      failed: { color: 'bg-red-500 text-white', icon: <XCircle className="h-3 w-3" /> },
      skipped: { color: 'bg-red-400 text-white', icon: <SkipForward className="h-3 w-3" /> },
      pending: { color: 'bg-slate-400 text-white', icon: <Clock className="h-3 w-3" /> },
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1 capitalize`}>
        {config.icon}
        {status}
      </Badge>
    );
  };

  const getRemarksColor = (status, remarks) => {
    if (status === 'completed') return 'text-green-600 dark:text-green-400';
    if (status === 'failed' || status === 'skipped') return 'text-red-600 dark:text-red-400';
    if (status === 'processing') return 'text-orange-600 dark:text-orange-400';
    return 'text-muted-foreground';
  };

  const summary = queueData?.summary || {};
  const totalProgress = summary.total > 0 
    ? ((summary.completed + summary.skipped) / summary.total) * 100 
    : 0;

  // Minimized view
  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-4 right-4 z-50 bg-background border rounded-lg shadow-lg p-3 cursor-pointer hover:shadow-xl transition-shadow"
        onClick={onMinimize}
        data-testid="processing-viewer-minimized"
      >
        <div className="flex items-center gap-3">
          <div className="relative">
            <FileAudio className="h-5 w-5 text-primary" />
            {summary.processing > 0 && (
              <span className="absolute -top-1 -right-1 h-2 w-2 bg-orange-500 rounded-full animate-pulse" />
            )}
          </div>
          <div className="text-sm">
            <div className="font-medium">Processing Calls</div>
            <div className="text-xs text-muted-foreground">
              {summary.completed || 0} / {summary.total || 0} completed
            </div>
          </div>
          <Progress value={totalProgress} className="w-20 h-2" />
          <Maximize2 className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-6xl h-[85vh] flex flex-col p-0" data-testid="processing-viewer-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <FileAudio className="h-5 w-5 text-primary" />
              Call Processing Status
              {summary.processing > 0 && (
                <Badge variant="secondary" className="bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300">
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Processing
                </Badge>
              )}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAutoScrollEnabled(!autoScrollEnabled)}
                className={autoScrollEnabled ? 'text-primary' : 'text-muted-foreground'}
              >
                {autoScrollEnabled ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                Auto-scroll
              </Button>
              <Button variant="ghost" size="sm" onClick={fetchQueueDetails} disabled={isLoading}>
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="ghost" size="sm" onClick={onMinimize}>
                <Minimize2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Summary Stats */}
          <div className="grid grid-cols-6 gap-3 mt-4">
            <Card className="p-3">
              <div className="text-2xl font-bold">{summary.total || 0}</div>
              <div className="text-xs text-muted-foreground">Total</div>
            </Card>
            <Card className="p-3 border-orange-200 dark:border-orange-800">
              <div className="text-2xl font-bold text-orange-600">{summary.processing || 0}</div>
              <div className="text-xs text-muted-foreground">Processing</div>
            </Card>
            <Card className="p-3 border-green-200 dark:border-green-800">
              <div className="text-2xl font-bold text-green-600">{summary.completed || 0}</div>
              <div className="text-xs text-muted-foreground">Completed</div>
            </Card>
            <Card className="p-3 border-red-200 dark:border-red-800">
              <div className="text-2xl font-bold text-red-600">{summary.failed || 0}</div>
              <div className="text-xs text-muted-foreground">Failed</div>
            </Card>
            <Card className="p-3 border-slate-200 dark:border-slate-700">
              <div className="text-2xl font-bold text-slate-600">{summary.skipped || 0}</div>
              <div className="text-xs text-muted-foreground">Skipped</div>
            </Card>
            <Card className="p-3 border-blue-200 dark:border-blue-800">
              <div className="text-2xl font-bold text-blue-600">{summary.pending || 0}</div>
              <div className="text-xs text-muted-foreground">Pending</div>
            </Card>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-3">
            <Progress value={totalProgress} className="h-2" />
            <div className="text-xs text-muted-foreground mt-1 text-right">
              {totalProgress.toFixed(1)}% complete
            </div>
          </div>
        </DialogHeader>
        
        {/* Filters */}
        <div className="px-6 py-3 border-b flex items-center gap-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="skipped">Skipped</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
          
          <div className="text-sm text-muted-foreground">
            Showing {queueData?.items?.length || 0} of {queueData?.total || 0} items
          </div>
        </div>
        
        {/* Table */}
        <ScrollArea className="flex-1 px-6" ref={scrollRef}>
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead className="w-16">#</TableHead>
                <TableHead className="min-w-[250px]">File Name</TableHead>
                <TableHead className="w-24">Size</TableHead>
                <TableHead className="w-28">Status</TableHead>
                <TableHead className="min-w-[200px]">Remarks</TableHead>
                <TableHead className="w-24">Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {queueData?.items?.map((item, idx) => (
                <TableRow 
                  key={item.call_id}
                  ref={item.status === 'processing' ? processingRowRef : null}
                  className={`
                    ${item.status === 'processing' ? 'bg-orange-50 dark:bg-orange-950/30 animate-pulse' : ''}
                    ${item.status === 'completed' ? 'bg-green-50/50 dark:bg-green-950/20' : ''}
                    ${item.status === 'failed' ? 'bg-red-50/50 dark:bg-red-950/20' : ''}
                  `}
                  data-testid={`queue-item-${item.call_id}`}
                >
                  <TableCell className="font-mono text-sm">{item.file_no}</TableCell>
                  <TableCell className="font-medium truncate max-w-[250px]" title={item.filename}>
                    {item.filename}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{item.size}</TableCell>
                  <TableCell>{getStatusBadge(item.status)}</TableCell>
                  <TableCell className={`text-sm ${getRemarksColor(item.status, item.remarks)}`}>
                    <div className="truncate max-w-[200px]" title={item.remarks}>
                      {item.remarks}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {item.time_taken_str !== '-' ? (
                      <span className="flex items-center gap-1">
                        <Timer className="h-3 w-3" />
                        {item.time_taken_str}
                      </span>
                    ) : '-'}
                  </TableCell>
                </TableRow>
              ))}
              
              {(!queueData?.items || queueData.items.length === 0) && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    {isLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading queue data...
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <AlertCircle className="h-8 w-8" />
                        No items in processing queue
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
        
        {/* Pagination */}
        {queueData?.total_pages > 1 && (
          <div className="px-6 py-3 border-t flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Page {currentPage} of {queueData.total_pages}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(queueData.total_pages, p + 1))}
                disabled={currentPage >= queueData.total_pages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ProcessingStatusViewer;
